/**
 * Shared carousel behaviour for the Knuska sections.
 *
 * Scrolling itself is CSS scroll-snap, so the section works without this file.
 * This only adds the arrows: it wires them to the track, keeps their disabled
 * state in step with the scroll position, and hides them when nothing overflows.
 */
if (!customElements.get('knuska-carousel')) {
  customElements.define(
    'knuska-carousel',
    class KnuskaCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        /*
         * Controls inside this carousel win over controls elsewhere in the
         * section. A section with one track can keep its arrows in the header,
         * which is where every section that predates this did put them; a
         * section with several tracks - collection tabs, one per panel - would
         * otherwise bind all of them to the first set it found.
         */
        const section = this.closest('.shopify-section') || this.parentElement;
        this.arrows =
          this.querySelector('[data-carousel-arrows]') ||
          (section ? section.querySelector('[data-carousel-arrows]') : null);
        this.prev = this.arrows ? this.arrows.querySelector('[data-carousel-prev]') : null;
        this.next = this.arrows ? this.arrows.querySelector('[data-carousel-next]') : null;
        this.dots =
          this.querySelector('[data-carousel-dots]') ||
          (section ? section.querySelector('[data-carousel-dots]') : null);

        this.onScroll = this.debounce(this.update.bind(this), 80);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
        if (this.next) this.next.addEventListener('click', () => this.step(1));

        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(this.debounce(this.update.bind(this), 120));
          this.observer.observe(this.track);
        } else {
          window.addEventListener('resize', this.onScroll);
        }

        this.update();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('resize', this.onScroll);
      }

      debounce(fn, wait) {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      }

      /** Width of one item plus the gap, so a click moves a whole page of items. */
      pageWidth() {
        const item = this.track.firstElementChild;
        if (!item) return this.track.clientWidth;
        const styles = getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        const perView = Math.max(1, Math.round(this.track.clientWidth / (item.offsetWidth + gap)));
        return (item.offsetWidth + gap) * perView;
      }

      step(direction) {
        this.track.scrollBy({ left: this.pageWidth() * direction, behavior: 'smooth' });
      }

      pageCount() {
        const page = this.pageWidth();
        if (!page) return 1;
        return Math.max(1, Math.ceil((this.track.scrollWidth - 1) / page));
      }

      buildDots(count) {
        if (this.dots.childElementCount === count) return;
        this.dots.textContent = '';
        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel-dot';
          dot.setAttribute('aria-label', `${i + 1}`);
          dot.addEventListener('click', () => {
            this.track.scrollTo({ left: this.pageWidth() * i, behavior: 'smooth' });
          });
          this.dots.appendChild(dot);
        }
      }

      update() {
        const overflows = this.track.scrollWidth - this.track.clientWidth > 2;
        const scrolls = getComputedStyle(this.track).overflowX !== 'visible';
        const active = overflows && scrolls;
        const max = this.track.scrollWidth - this.track.clientWidth;
        const left = Math.round(this.track.scrollLeft);

        if (this.arrows) {
          this.arrows.hidden = !active;
          if (active) {
            if (this.prev) this.prev.disabled = left <= 1;
            if (this.next) this.next.disabled = left >= max - 1;
          }
        }

        if (this.dots) {
          this.dots.hidden = !active;
          if (!active) return;
          const pages = this.pageCount();
          this.buildDots(pages);
          const current = Math.min(pages - 1, Math.round(left / (this.pageWidth() || 1)));
          Array.from(this.dots.children).forEach((dot, i) => {
            dot.classList.toggle('is-active', i === current);
            dot.setAttribute('aria-current', i === current ? 'true' : 'false');
          });
        }
      }
    }
  );
}
